import { useCallback, useMemo, useState } from "react";
import { isEntityLockedTaskType, Task } from "@common/types/task";
import type { TaskBulkUpdateAction } from "@common/schemas/task.schema";
import { useBulkUpdateTasks, useBulkDeleteTasks } from "@api/task.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { useWorkspaceTaskGroups } from "@api/taskGroup.api";
import { useEntities } from "@hooks/useEntities";
import type { CheckboxIndicatorState } from "@design-system/Checkbox/CheckboxIndicator";
import { useCommands } from "@components/commands/useCommands";
import {
    CommandsDirectory,
    EntityBulkCommandOption,
    InjectedCommandOption,
    TaskEntityBulkOperation,
} from "@components/commands/lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    buildTaskBulkPriorityOptions,
    buildTaskBulkStatusOptions,
} from "./taskBulkCommandOptions";
import { useTaskBulkActionsOpenOverride } from "./useTaskBulkActionsOpenOverride";

interface UseTaskBulkActionsParams {
    selectedTaskIds: string[];
    selectedTasks: Task[];
    clearSelection: () => void;
}

export function useTaskBulkActions({
    selectedTaskIds,
    selectedTasks,
    clearSelection,
}: UseTaskBulkActionsParams) {
    const { open: openCommands } = useCommands();
    const { error: showErrorToast } = useToaster();
    const bulkUpdateTasks = useBulkUpdateTasks();
    const bulkDeleteTasks = useBulkDeleteTasks();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const { data: workspaceMembers = [] } = useCurrentWorkspaceMembers();
    const { data: workspaceTaskGroups = [] } = useWorkspaceTaskGroups();
    const workspaceEntities = useEntities();

    const handleBulkUpdateError = useCallback(() => {
        showErrorToast("Failed to update tasks");
    }, [showErrorToast]);

    const applyBulkUpdate = useCallback(
        (action: TaskBulkUpdateAction) => {
            bulkUpdateTasks.mutate(
                { taskIds: selectedTaskIds, action },
                {
                    onSuccess: clearSelection,
                    onError: handleBulkUpdateError,
                },
            );
        },
        [
            bulkUpdateTasks,
            clearSelection,
            handleBulkUpdateError,
            selectedTaskIds,
        ],
    );

    const statusOptions = useMemo(
        () =>
            buildTaskBulkStatusOptions({
                isPending: bulkUpdateTasks.isPending,
                applyAction: applyBulkUpdate,
            }),
        [bulkUpdateTasks.isPending, applyBulkUpdate],
    );

    const assignMemberOptions = useMemo((): InjectedCommandOption[] => {
        const unassignOption: InjectedCommandOption = {
            id: "unassign-member",
            label: "Unassign",
            keywords: ["remove", "clear", "assignee"],
            disabled: bulkUpdateTasks.isPending,
            onSelect: () => {
                bulkUpdateTasks.mutate(
                    {
                        taskIds: selectedTaskIds,
                        action: { type: "assignee", assigneeId: null },
                    },
                    {
                        onSuccess: clearSelection,
                        onError: handleBulkUpdateError,
                    },
                );
            },
        };

        const memberOptions = workspaceMembers.map((member) => {
            const name =
                member.user.name ||
                `${member.user.firstName} ${member.user.lastName}`.trim();

            return {
                id: `assign-member-${member.user.id}`,
                label: name,
                keywords: [name, member.user.email ?? ""].filter(Boolean),
                disabled: bulkUpdateTasks.isPending,
                onSelect: () => {
                    bulkUpdateTasks.mutate(
                        {
                            taskIds: selectedTaskIds,
                            action: {
                                type: "assignee",
                                assigneeId: member.user.id,
                            },
                        },
                        {
                            onSuccess: clearSelection,
                            onError: handleBulkUpdateError,
                        },
                    );
                },
            } satisfies InjectedCommandOption;
        });

        return [unassignOption, ...memberOptions];
    }, [
        bulkUpdateTasks,
        clearSelection,
        handleBulkUpdateError,
        selectedTaskIds,
        workspaceMembers,
    ]);

    const priorityOptions = useMemo(
        () =>
            buildTaskBulkPriorityOptions({
                isPending: bulkUpdateTasks.isPending,
                applyAction: applyBulkUpdate,
            }),
        [bulkUpdateTasks.isPending, applyBulkUpdate],
    );

    const taskGroupOptions = useMemo(():
        | InjectedCommandOption[]
        | undefined => {
        if (!workspaceTaskGroups.length) {
            return undefined;
        }

        const noGroupOption: InjectedCommandOption = {
            id: "task-group-none",
            label: "No group",
            keywords: ["none", "clear", "remove group"],
            disabled: bulkUpdateTasks.isPending,
            onSelect: () => {
                bulkUpdateTasks.mutate(
                    {
                        taskIds: selectedTaskIds,
                        action: { type: "taskGroup", groupId: null },
                    },
                    {
                        onSuccess: clearSelection,
                        onError: handleBulkUpdateError,
                    },
                );
            },
        };

        const groupOptions: InjectedCommandOption[] = workspaceTaskGroups.map(
            (taskGroup) => ({
                id: `task-group-${taskGroup.id}`,
                label: taskGroup.name,
                keywords: [taskGroup.name],
                disabled: bulkUpdateTasks.isPending,
                onSelect: () => {
                    bulkUpdateTasks.mutate(
                        {
                            taskIds: selectedTaskIds,
                            action: {
                                type: "taskGroup",
                                groupId: taskGroup.id,
                            },
                        },
                        {
                            onSuccess: clearSelection,
                            onError: handleBulkUpdateError,
                        },
                    );
                },
            }),
        );

        return [noGroupOption, ...groupOptions];
    }, [
        bulkUpdateTasks,
        clearSelection,
        handleBulkUpdateError,
        selectedTaskIds,
        workspaceTaskGroups,
    ]);

    // Some task types (e.g. transactions-based tasks) can never change
    // entity server-side (see isEntityLockedTaskType) — exclude them from the
    // entity multi-select entirely so the Apply button never targets a task
    // that will just reject the change, and surface the excluded count in the
    // command heading instead.
    const entityUnlockedTasks = useMemo(
        () => selectedTasks.filter((t) => !isEntityLockedTaskType(t.type)),
        [selectedTasks],
    );
    const entityUnlockedTaskIds = useMemo(
        () => entityUnlockedTasks.map((t) => t.id),
        [entityUnlockedTasks],
    );
    const taskBulkEntityLockedCount =
        selectedTasks.length - entityUnlockedTasks.length;

    // Mirrors the trx bulk "multi class selection, single apply button" UX:
    // checkbox state reflects the *current* assignment across the eligible
    // (unlocked) tasks, but toggling only edits local staged selection —
    // nothing is persisted until Apply fires a single batch of add/remove
    // calls for just the entities the user actually changed.
    const entityOptions = useMemo((): EntityBulkCommandOption[] => {
        if (!workspaceEntities.length || !entityUnlockedTasks.length) {
            return [];
        }

        return workspaceEntities.map((entity) => {
            const tasksWithEntity = entityUnlockedTasks.filter((t) =>
                t.entityIds?.includes(entity.id),
            );
            const count = tasksWithEntity.length;
            let checkboxState: CheckboxIndicatorState = "unchecked";
            if (count === entityUnlockedTasks.length) {
                checkboxState = "checked";
            } else if (count > 0) {
                checkboxState = "indeterminate";
            }

            return { entity, checkboxState };
        });
    }, [workspaceEntities, entityUnlockedTasks]);

    // The command list stages the add/remove edits and computes the diff
    // against the same option state it renders, so here we just forward the
    // batch. It is applied as a single atomic request — a mid-batch failure
    // rolls back server-side rather than leaving tasks partially updated.
    const applyEntities = useCallback(
        (operations: TaskEntityBulkOperation[]) => {
            if (!entityUnlockedTaskIds.length || !operations.length) {
                return;
            }
            bulkUpdateTasks.mutate(
                {
                    taskIds: entityUnlockedTaskIds,
                    action: { type: "entity", operations },
                },
                {
                    onSuccess: clearSelection,
                    onError: handleBulkUpdateError,
                },
            );
        },
        [
            entityUnlockedTaskIds,
            bulkUpdateTasks,
            clearSelection,
            handleBulkUpdateError,
        ],
    );

    const handleSetStatusClick = useCallback(() => {
        if (!selectedTaskIds.length) {
            return;
        }

        openCommands(CommandsDirectory.TASK_BULK_STATUS, {
            taskBulkStatusOptions: statusOptions,
            selectedTaskCount: selectedTaskIds.length,
        });
    }, [openCommands, selectedTaskIds.length, statusOptions]);

    const handleAssignMemberClick = useCallback(() => {
        if (!selectedTaskIds.length) {
            return;
        }

        openCommands(CommandsDirectory.TASK_BULK_MEMBER, {
            taskBulkMemberOptions: assignMemberOptions,
            selectedTaskCount: selectedTaskIds.length,
        });
    }, [assignMemberOptions, openCommands, selectedTaskIds.length]);

    const handleBulkDeleteConfirm = useCallback(async () => {
        await bulkDeleteTasks.mutateAsync(selectedTaskIds, {
            onSuccess: () => {
                clearSelection();
                setShowDeleteConfirm(false);
            },
            onError: () => {
                showErrorToast("Failed to delete tasks");
                setShowDeleteConfirm(false);
            },
        });
    }, [bulkDeleteTasks, clearSelection, selectedTaskIds, showErrorToast]);

    const handleOpenBulkActions = useCallback(() => {
        openCommands(CommandsDirectory.TASK_BULK, {
            taskBulkStatusOptions: statusOptions,
            taskBulkMemberOptions: assignMemberOptions,
            taskBulkPriorityOptions: priorityOptions,
            taskBulkTaskGroupOptions: taskGroupOptions,
            taskBulkEntityOptions:
                entityOptions.length > 0 ? entityOptions : undefined,
            taskBulkApplyEntities:
                entityOptions.length > 0 ? applyEntities : undefined,
            taskBulkEntityLockedCount,
            onTaskBulkDelete: () => setShowDeleteConfirm(true),
            selectedTaskCount: selectedTaskIds.length,
        });
    }, [
        openCommands,
        statusOptions,
        assignMemberOptions,
        priorityOptions,
        taskGroupOptions,
        entityOptions,
        applyEntities,
        taskBulkEntityLockedCount,
        selectedTaskIds.length,
    ]);

    useTaskBulkActionsOpenOverride({
        selectedCount: selectedTaskIds.length,
        openBulkActions: handleOpenBulkActions,
    });

    return {
        showDeleteConfirm,
        setShowDeleteConfirm,
        handleSetStatusClick,
        handleAssignMemberClick,
        handleBulkDeleteConfirm,
        handleOpenBulkActions,
    };
}
